/**
 * Price labels (App Review brief item 4, 17 Oct 2026; guideline 3.1.1 —
 * the price a user sees must be the STORE's price for their storefront).
 *
 * `StorePrices` is what the billing provider reads from the store's
 * offerings: one label per sellable thing — the three plans AND the scan
 * pack, which until now showed a hard-coded "$0.99" to every storefront.
 *
 * `priceLabel()` decides what to render:
 *   - the store's label when it has arrived;
 *   - on NATIVE without it: a non-currency placeholder — never a US-dollar
 *     figure a reviewer in another storefront can call wrong;
 *   - on WEB (no store; the hard-coded figures are the marketing truth):
 *     the PLANS/SCAN_PACK figure as before.
 */
import type { PaidPlan, PlanMeta } from '../types';
import { isNative } from '../native/native';

export type StorePriceKey = PaidPlan | 'scanPack';
export type StorePrices = Partial<Record<StorePriceKey, string>>;

/** Shown on native while (or if) the store has not answered. */
export const STORE_PRICE_PENDING = 'Price shown at checkout';

export function priceLabel(store: string | undefined, webFallback: string, native: boolean = isNative()): string {
  if (store) return store;
  return native ? STORE_PRICE_PENDING : webFallback;
}

/** Settings' "current plan" line: the store's price when known, the web
 *  figure on web, and on native with no store answer the title alone
 *  (a placeholder would read oddly next to a plan already paid for). */
export function planTitleWithPrice(plan: PlanMeta, store: string | undefined, native: boolean = isNative()): string {
  const price = store ?? (native ? '' : plan.price);
  return price ? `${plan.title} — ${price}` : plan.title;
}
